import { useState, useEffect, useCallback } from 'react';
import qs from 'qs';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Title,
  Grid,
  TextInput,
  Button,
  Table,
  Pagination,
  Group,
  Box,
  Text,
  ActionIcon,
  Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconSearch, IconDownload, IconFileSearch, IconAlertTriangle } from '@tabler/icons-react';

import * as actions from '../../../actions/ArquivoDigitalizado';
import SmartMapLegend from '../../../components/Map/SmartMapLegend';
import SmartMap, { mapKey } from '../../../components/Map/SmartMapFallback';
import ListLoadingOverlay from '../../../components/ListLoadingOverlay';
import { TableSkeleton } from '../../../components/TableSkeleton';
import EmptyState from '../../../components/EmptyState';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { qsNumber, qsString } from '../../../utils/qsHelpers';

const DEFAULTS = { filterText: '', filterExtent: '', pageSize: 8, page: 1 };

interface ListFilters {
  SEARCH?: string;
  EXTENT?: unknown;
}

export default function ArquivosDigitalizadosList() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const results = useAppSelector((state) => state.arquivosDigitalizados.results);
  const listError = useAppSelector((state) => state.arquivosDigitalizados.error);
  const loading = useAppSelector((state) => state.container.loading);
  const publicSettings = useAppSelector((state) => state.publicSettings.settings);

  const [searchValue, setSearchValue] = useState('');
  const [filterText, setFilterText] = useState('');
  const [filterExtent, setFilterExtent] = useState('');
  const [page, setPage] = useState<number>(DEFAULTS.page);
  const [pageSize] = useState<number>(DEFAULTS.pageSize);
  const [geoJson, setGeoJson] = useState<unknown[] | unknown | null>(null);

  const doSearch = useCallback((fText: string, fExtent: string, pg: number) => {
    const query: Record<string, unknown> = { page: pg, pageSize };
    if (fText) query.q = fText;
    else if (fExtent) query.e = fExtent;
    navigate('/arquivos-digitalizados?' + qs.stringify(query));
  }, [navigate, pageSize]);

  const executeSearch = useCallback(() => {
    const query = qs.parse(location.search, { ignoreQueryPrefix: true });

    const pg = qsNumber(query.page, DEFAULTS.page);
    const ps = qsNumber(query.pageSize, DEFAULTS.pageSize);
    const fText = qsString(query.q, DEFAULTS.filterText);
    const fExtent = qsString(query.e, DEFAULTS.filterExtent);

    const filters: ListFilters = {};
    if (fText.length > 0) filters.SEARCH = fText;
    else if (fExtent) filters.EXTENT = JSON.parse(`[${fExtent}]`);

    setPage(pg);
    setFilterText(fText);
    setFilterExtent(fExtent);
    if (fText) setSearchValue(fText);

    dispatch((actions as any).list({ page: pg, pageSize: ps, filterText: fText, filterExtent: fExtent, filters }));
  }, [location.search, dispatch]);

  useEffect(() => {
    dispatch((actions as any).clearResults());
  }, []);

  useEffect(() => {
    if (location.search) executeSearch();
  }, [location.key]);

  useEffect(() => {
    if (results?.items) {
      const data = results.items;
      const items = data.length === 1 ? data : data.filter((x) => x.tipo !== 'MUNICIPIO');
      setGeoJson(items.map((x) => x.geoJson));
    } else if (results) {
      setGeoJson([]);
    }
  }, [results]);

  const clickSearch = () => {
    if (!searchValue || searchValue.trim().length === 0) {
      notifications.show({ message: 'É necessário informar um valor para busca.', color: 'yellow' });
      return;
    }
    setPage(1);
    setFilterText(searchValue);
    setFilterExtent('');
    setGeoJson(null);
    doSearch(searchValue, '', 1);
  };

  const identifyOnExtent = (geographicExtent: number[]) => {
    const fExtent = JSON.stringify(geographicExtent).slice(1, -1);
    setPage(1);
    setFilterText('');
    setFilterExtent(fExtent);
    setGeoJson(null);
    doSearch('', fExtent, 1);
  };

  const total = !results ? 0 : results.total;
  const dataSource = !results ? [] : results.items;
  const menorRnum = ((page - 1) * pageSize) + 1;
  let maoirRnum = page * pageSize;
  if (results) maoirRnum = maoirRnum < results.total ? maoirRnum : results.total;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  let filterMessage: string | null = null;
  if (filterText?.length > 0) filterMessage = `Filtrado por "${filterText}"`;
  else if (filterExtent?.length > 0) filterMessage = 'Filtrado espacialmente';

  return (
    <Box>
      <Title order={1}>Listar {publicSettings?.arquivosDigitalizadosAbaName}</Title>

      <Grid gap="md" mt="md">
        <Grid.Col span={{ base: 12, md: 7, lg: 7 }} offset={{ base: 0, md: 2, lg: 2 }}>
          <TextInput
            disabled={loading}
            autoFocus
            maxLength={300}
            placeholder="Nome do arquivo, localização"
            leftSection={<IconSearch size={16} />}
            value={searchValue || ''}
            onChange={(e) => setSearchValue(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') clickSearch(); }}
            rightSectionWidth={92}
            rightSection={
              <Button size="xs" onClick={clickSearch} leftSection={<IconSearch size={14} />}>
                Buscar
              </Button>
            }
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 7, lg: 7 }} offset={{ base: 0, md: 2, lg: 2 }}>
          <Group justify="space-between">
            <Text fw={600}>{filterMessage}</Text>
            {results && results.total > 0 && (
              <Text size="sm" c="dimmed">
                Listando {menorRnum} - {maoirRnum} de {results.total} registros.
              </Text>
            )}
          </Group>
        </Grid.Col>
      </Grid>

      <Grid gap="md" mt="md">
        <Grid.Col span={{ base: 12, md: 6 }}>
          <ListLoadingOverlay loading={loading}>
            {(!loading && listError) ? (
              <EmptyState
                label="Não foi possível carregar os arquivos"
                submessage={listError}
                icon={IconAlertTriangle}
                mih={200}
              />
            ) : (!loading && results && dataSource.length === 0) ? (
              <EmptyState
                label="Nenhum arquivo encontrado"
                submessage={filterMessage ? 'Tente outro filtro ou área no mapa.' : 'Use a busca acima para começar.'}
                icon={IconFileSearch}
                mih={200}
              />
            ) : (
              <>
                <Table striped highlightOnHover withTableBorder>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Localização</Table.Th>
                      <Table.Th style={{ width: '20%' }}>Tipo</Table.Th>
                      <Table.Th>Nome do Arquivo</Table.Th>
                      <Table.Th style={{ width: 100 }}>Ações</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {loading && dataSource.length === 0 ? (
                      <TableSkeleton columns={['80%', '58%', '76%', '54%']} />
                    ) : dataSource.map((item) => (
                      <Table.Tr key={`${item.id}__${item.arquivo ? item.arquivo.id : 'no_file'}`}>
                        <Table.Td>
                          <Text size="sm" lineClamp={1} title={item.atributos[0]}>{item.atributos[0]}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm" lineClamp={1} title={item.tipo}>{item.tipo}</Text>
                        </Table.Td>
                        <Table.Td>
                          {item.arquivo && (
                            <Text size="sm" lineClamp={1} title={item.arquivo.nome}>{item.arquivo.nome}</Text>
                          )}
                        </Table.Td>
                        <Table.Td>
                          <Group gap={4} wrap="nowrap">
                            <Tooltip label="Localizar">
                              <ActionIcon variant="subtle" onClick={() => setGeoJson(item.geoJson)}>
                                <IconSearch size={16} />
                              </ActionIcon>
                            </Tooltip>
                            {item.arquivo && (
                              <Tooltip label="Download">
                                <ActionIcon variant="subtle" onClick={() => window.open(item.arquivo!.url, '_blank')}>
                                  <IconDownload size={16} />
                                </ActionIcon>
                              </Tooltip>
                            )}
                          </Group>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
                {totalPages > 1 && (
                  <Group justify="center" mt="md">
                    <Pagination
                      total={totalPages}
                      value={page}
                      onChange={(p) => { setPage(p); doSearch(filterText, filterExtent, p); }}
                    />
                  </Group>
                )}
              </>
            )}
          </ListLoadingOverlay>
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 6 }} style={{ display: 'flex', flexDirection: 'column', minHeight: 'calc(100vh - 330px)' }}>
          <Box style={{ position: 'relative', flex: 1 }}>
            <SmartMap
              mapKey={mapKey.PUBLIC_ARQUIVOS_DIGITALIZADOS}
              identifyOnExtent={identifyOnExtent}
              geoJson={geoJson}
            />
          </Box>
          <SmartMapLegend />
        </Grid.Col>
      </Grid>
    </Box>
  );
}
